import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { updateAvatar } from '@/utils/storage';
import { withTimeout } from '@/utils/asyncTimeout';
import { syncAuthMetadata } from '@/utils/sync-auth-metadata';
import { requireAuth, AuthExpiredError } from '@/utils/require-auth';

interface ProfileData {
  first_name: string;
  last_name: string;
  phone_number: string | null;
  timezone: string | null;
  preferred_language: string | null;
  avatar_url: string | null;
}

export const useProfileManager = (userId: string, initialProfile: any) => {
  const { toast } = useToast();
  const [profile, setProfile] = useState(initialProfile);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const handleAvatarChange = (file: File | null, preview: string | null) => {
    console.log("Setting avatar file in useProfileManager:", file?.name, file?.type);
    setAvatarFile(file);
    setAvatarPreview(preview);
  };

  const refreshProfileData = async () => {
    if (!userId) return;
    
    try {
      const { data, error } = await withTimeout(
        supabase
          .from('clients')
          .select('*')
          .eq('id', userId)
          .single()
      );
        
      if (error) throw error;
      
      setProfile(data);
      return data;
    } catch (error) {
      console.error('Error refreshing profile:', error);
      throw error;
    }
  };

  const updateProfile = async (values: ProfileData): Promise<boolean> => {
    if (!userId || !profile) return false;
    
    try {
      setIsSaving(true);
      setError(null);

      // Verify session is still active before saving
      await requireAuth();
      
      let avatarPath = profile.avatar_url;
      
      // Only upload the avatar if a new file is selected
      if (avatarFile) {
        try {
          console.log("Avatar file being uploaded:", avatarFile);
          
          const { path } = await updateAvatar('client.photo', userId, avatarFile);
          avatarPath = path;
          
        } catch (error: any) {
          console.error('Error updating avatar:', error);
          toast({
            title: "Upload failed",
            description: error.message || "Failed to upload avatar image. Please try again.",
            variant: "destructive",
            duration: 8000,
          });
          return false;
        }
      }
      
      // Derive E.164 from the space-separated phone_number (e.g. "+1 9099057788" → "+19099057788")
      const phoneE164 = values.phone_number
        ? values.phone_number.replace(/\s+/g, '')
        : null;

      // Update the client record in the database
      const updatePayload: Record<string, any> = {
        first_name: values.first_name,
        last_name: values.last_name,
        phone_number: values.phone_number,
        phone_e164: phoneE164,
        preferred_language: values.preferred_language,
        avatar_url: avatarPath,
        updated_at: new Date().toISOString(),
      };

      // Only persist timezone when the user explicitly selected one.
      if (typeof values.timezone === 'string' && values.timezone.trim()) {
        updatePayload.timezone = values.timezone.trim();
      }

      const { error: updateError } = await withTimeout(
        supabase
          .from('clients')
          .update(updatePayload)
          .eq('id', userId)
      );
        
      if (updateError) {
        console.error('Error updating profile in database:', updateError);
        throw updateError;
      }
      
      // Refresh the profile data from the database
      await refreshProfileData();

      // Sync name to auth metadata (fire-and-forget)
      syncAuthMetadata({ first_name: values.first_name, last_name: values.last_name });
      
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated",
        duration: 8000,
      });
      
      // Clear the avatar file state
      setAvatarFile(null);
      setAvatarPreview(null);
      
      return true;
      
    } catch (error: any) {
      console.error('Error updating profile:', error);
      const isAuthError = error instanceof AuthExpiredError;
      const msg = isAuthError
        ? 'Your session has expired. Please log in again.'
        : (error.message || "Failed to update profile");
      setError(msg);
      toast({
        title: isAuthError ? "Session expired" : "Update failed",
        description: msg,
        variant: "destructive",
        duration: 8000,
      });
      if (isAuthError) {
        setTimeout(() => { window.location.href = '/'; }, 2000);
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    profile,
    setProfile,
    error,
    setError,
    isSaving,
    avatarFile,
    avatarPreview,
    handleAvatarChange,
    refreshProfileData,
    updateProfile
  };
};
